import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';

import { JobRecurringDay } from 'us-web-services';
import { daysOfWeek } from '../constants/DaysOfWeek';
import { getTimeOfDayString } from '../utils/date';

const NIGHT_HOUR_THRESHOLD = 17;

const fmtHhMm = (time: string): string => {
  const parsed = new Date(`2000-01-01T${time}`);
  if (Number.isNaN(parsed.getTime())) return time;
  return getTimeOfDayString(parsed);
};

type DayIcon = 'day' | 'night' | null;

interface Props {
  days?: JobRecurringDay[];
  timeSummary?: string;
  flexible?: boolean;
  sameTimes?: boolean;
  timeOfDay?: { code?: string };
}

export const RecurringScheduleDisplay = ({
  days,
  timeSummary,
  flexible,
  sameTimes,
  timeOfDay,
}: Props): JSX.Element | null => {
  if (!days?.length && !flexible) return null;

  const dayById = new Map((days ?? []).map((day) => [day.weekday.id, day]));
  const hasDifferentTimes = (days ?? []).some((day) => day.startTime && day.endTime);
  const computedSameTimes =
    hasDifferentTimes &&
    (days ?? []).every(
      (day, _, allDays) => day.startTime === allDays[0].startTime && day.endTime === allDays[0].endTime,
    );
  const resolvedSameTimes = sameTimes ?? computedSameTimes;

  // Only an explicit "night" time-of-day (or specific hours resolving to
  // night) shows the moon — every other selected day, including an
  // unset/unrecognized time-of-day, defaults to the sun icon.
  const iconForDay = (day?: JobRecurringDay): DayIcon => {
    if (timeOfDay?.code === 'night') return 'night';
    if (timeOfDay?.code === 'specific' && day?.startTime && day?.endTime) {
      const startHour = Number(day.startTime.split(':')[0]);
      const endHour = Number(day.endTime.split(':')[0]);
      if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return 'day';
      if (startHour >= NIGHT_HOUR_THRESHOLD) return 'night';
      if (endHour <= NIGHT_HOUR_THRESHOLD) return 'day';
      if (NIGHT_HOUR_THRESHOLD - startHour >= endHour - NIGHT_HOUR_THRESHOLD) return 'day';
      return 'night';
    }
    return 'day';
  };

  return (
    <div className='mt-3'>
      <div className='flex gap-3 flex-wrap'>
        {daysOfWeek.map((day) => {
          const selectedDay = dayById.get(day.id);
          const selected = !!selectedDay;
          const icon = selected ? iconForDay(selectedDay) : null;
          return (
            <div key={day.id} className='flex flex-col items-center'>
              <span className='text-xs text-neutral-600 font-light'>{day.code}</span>
              <span
                aria-hidden='true'
                className={`mt-1 w-9 h-9 rounded-full flex items-center justify-center ${
                  !selected
                    ? 'bg-neutral-200'
                    : icon === 'night' ? 'bg-sapphire' : icon === 'day' ? 'bg-yellow-400' : 'bg-neutral-400'
                } ${flexible ? 'opacity-40' : ''}`}
              >
                {icon && (
                  <FontAwesomeIcon
                    icon={icon === 'night' ? faMoon : faSun}
                    className='text-white text-lg'
                  />
                )}
              </span>
            </div>
          );
        })}
      </div>
      {timeSummary && <div className='text-sm text-neutral-600 mt-2'>{timeSummary}</div>}
      {hasDifferentTimes && !resolvedSameTimes && (
        <ul className='text-sm text-neutral-600 mt-1 space-y-0.5'>
          {(days ?? []).map((day) => (
            <li key={day.weekday.id}>
              {day.weekday.code}: {day.startTime ? fmtHhMm(day.startTime) : ''} - {day.endTime ? fmtHhMm(day.endTime) : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
